import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useParentAuth } from "@/context/ParentAuthContext";
import { toast } from "sonner";
import { Calendar, MapPin, DollarSign, Lock, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function EventInvite() {
  const { token } = useParams();
  const { parent, loading: authLoading } = useParentAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [loadedToken, setLoadedToken] = useState("");
  const [retryClaim, setRetryClaim] = useState(0);
  const claimAttempt = useRef(null);
  const parentId = parent?.user_id;

  useEffect(() => {
    let active = true;
    setLoading(true); setError(""); setData(null); setLoadedToken(""); setClaimed(false); setClaimError("");
    api.get(`/events/invite/${token}`)
      .then((r) => { if (active) { setData(r.data); setLoadedToken(token); } })
      .catch((e) => { if (active) setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Invitation not found"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!parentId || loadedToken !== token) return;
    let active = true;
    const key = `${parentId}:${token}:${retryClaim}`;
    // Reuse the in-flight attempt during Strict Mode remounts; retry only on demand.
    if (claimAttempt.current?.key !== key) claimAttempt.current = { key, promise: api.post(`/events/invite/${token}/claim`) };
    setClaiming(true); setClaimed(false); setClaimError("");
    claimAttempt.current.promise
      .then(() => { if (active) { setClaimed(true); toast.success("Invitation accepted"); } })
      .catch((e) => { if (active) setClaimError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not accept this invitation. Please try again."); })
      .finally(() => { if (active) setClaiming(false); });
    return () => { active = false; };
  }, [parentId, loadedToken, token, retryClaim]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFFFC] grid place-items-center px-5">
        <div className="text-[#7A7D8C] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading invitation…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFFFC] grid place-items-center px-5 py-16">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 p-8 text-center" data-testid="invite-error">
          <h1 className="font-display font-black text-2xl">Invitation unavailable</h1>
          <p className="mt-2 text-[#7A7D8C]">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white font-bold text-sm">Home</Link>
        </div>
      </div>
    );
  }
  const ev = data.event;
  const invite = data.invite;
  const suggestedEmail = invite.email;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFBF69]/20 via-[#FDFFFC] to-[#FDE74C]/25 px-5 py-12 md:py-20">
      <div className="max-w-xl mx-auto">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FDE74C]/60 text-[#B36906] text-[11px] uppercase tracking-widest font-black" data-testid="invite-mock-chip"><Lock className="w-3 h-3" /> Private invitation</span>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="invite-event-title">{ev.title}</h1>
          <p className="mt-3 text-[#7A7D8C]">You&apos;ve been invited to a private Naptown Cubers {ev.kind === "camp" ? "camp" : "competition"}.</p>
        </div>

        <div className="mt-10 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_10px_40px_rgb(0,0,0,0.05)]">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#FF9F1C]" /><span>{fmtDate(ev.start_date)}{ev.end_date && ev.end_date !== ev.start_date ? ` – ${fmtDate(ev.end_date)}` : ""}</span></div>
            {ev.location && <div className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF9F1C]" /><span>{ev.location}</span></div>}
            {ev.registration_fee != null && <div className="inline-flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-[#FF9F1C]" /><span>${ev.registration_fee}</span></div>}
          </div>
          {ev.description && <p className="mt-5 text-sm text-[#1E1E24]/90 whitespace-pre-wrap leading-relaxed">{ev.description}</p>}

          <div className="mt-8 border-t border-slate-100 pt-6">
            {authLoading ? (
              <div className="text-sm text-[#7A7D8C] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking your session…</div>
            ) : !parent ? (
              <div data-testid="invite-signin">
                <p className="text-sm text-[#7A7D8C]">Sign in with <b className="text-[#1E1E24]">{suggestedEmail}</b> to accept this invitation. New here? Create a parent account and it&apos;ll auto-link.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/parent/login?next=${encodeURIComponent(`/events/invite/${token}`)}`} data-testid="invite-login-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF9F1C] text-white font-bold text-sm shadow-[0_8px_20px_rgb(255,159,28,0.3)]">Sign in <ArrowRight className="w-4 h-4" /></Link>
                  <Link to={`/parent/signup?next=${encodeURIComponent(`/events/invite/${token}`)}`} data-testid="invite-signup-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 font-bold text-sm hover:border-[#FF9F1C]">Create account</Link>
                </div>
              </div>
            ) : claimed ? (
              <div data-testid="invite-accepted">
                <div className="inline-flex items-center gap-2 text-[#0F7A72] font-semibold"><CheckCircle2 className="w-5 h-5" /> Invitation accepted — this event is now in your dashboard.</div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to={`/register?event=${ev.id}&invite=${token}`} data-testid="invite-register-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF9F1C] text-white font-bold text-sm shadow-[0_8px_20px_rgb(255,159,28,0.3)]">Register a cuber <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/parent" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 font-bold text-sm hover:border-[#FF9F1C]">My dashboard</Link>
                </div>
              </div>
            ) : claimError ? (
              <div role="alert" className="text-sm text-red-800" data-testid="invite-claim-error">
                <p>{claimError}</p>
                <button type="button" onClick={() => setRetryClaim((value) => value + 1)} className="mt-3 font-semibold underline" data-testid="invite-retry-claim">Try again</button>
              </div>
            ) : (
              <div className="text-sm text-[#7A7D8C] inline-flex items-center gap-2" data-testid="invite-claiming"><Loader2 className="w-4 h-4 animate-spin" /> {claiming ? "Accepting invitation…" : "Preparing invitation…"}</div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[#7A7D8C]">This link is private — please don&apos;t share it outside your family.</p>
      </div>
    </div>
  );
}
